import _ from 'lodash';
import { DEFAULT_SEARCH_RADIUS } from '../constants';
import { RADIUS_FILTER } from '../data/filters/filters';
import { findValidFilters } from '../pages/api/filters.page';

export interface Filter {
  key: string;
  label: string;
  type: string;
  disabled?: boolean;
  immutable?: boolean;
  autoChecked?: boolean;
}

export interface FilterCheckbox extends Filter {
  urlKey: string;
}

interface FilterMinMax {
  min: '' | number;
  max: '' | 'Any Value' | number;
}

export interface FilterSortBy extends Filter {
  urlKey: string;
}

export interface FilterInput extends Filter {
  parent?: string;
  value: string | number;
}

export interface FilterSet {
  onlyOfficeAddress: boolean;
  constructionSector: FilterCheckbox[];
  companyType: FilterCheckbox[];
  trade: FilterCheckbox[];
  projectType: FilterCheckbox[];
  projectDetails: FilterCheckbox[];
  fundingType: FilterCheckbox[];
  contractValue: FilterMinMax;
  laborType: FilterCheckbox[];
  classifications: FilterCheckbox[];
  radius: FilterInput;
  sortBy: FilterSortBy;
}

export interface FilterState {
  filters: FilterSet;
  updateCheckbox: (item: FilterCheckbox) => void;
  updateCheckboxBulk: (items: Array<FilterCheckbox>, checked: boolean) => void;
  updateSortBy: (item: FilterSortBy) => void;
  updateInput: (item: FilterInput) => void;
  updateOnlyOfficeAddress: (item: boolean) => void;
  clearFilters: () => void;
}

export const filterDefaults = (projectsSearch = false): FilterSet => ({
  onlyOfficeAddress: false,
  constructionSector: [],
  companyType: [],
  trade: [],
  projectType: [],
  projectDetails: [],
  fundingType: [],
  contractValue: {
    min: '',
    max: '',
  },
  laborType: [],
  classifications: [],
  radius: { ...RADIUS_FILTER, value: DEFAULT_SEARCH_RADIUS },
  // istanbul ignore next
  sortBy: projectsSearch
    ? // istanbul ignore next
      {
        key: 'last_updated',
        label: 'Last Updated',
        urlKey: 'last_updated',
        type: 'sortBy',
      }
    : // istanbul ignore next
      {
        key: 'relevance',
        label: 'Relevance',
        urlKey: 'relevance',
        type: 'sortBy',
      },
});

export enum FilterUrlKey {
  onlyOfficeAddress = 'only-office-address',
  constructionSector = 'sectors',
  companyType = 'types',
  trade = 'trades',
  classification = 'classifications',
  projectDetails = 'project-details',
  radius = 'radius',
  fundingType = 'sector-type',
}

export const getValidFiltersFromQuery = async (
  query: Partial<{
    slug?: Array<string>;
    trades?: string;
    sectors?: string;
    types?: string;
    classifications?: string;
    radius?: string;
  }>,
  locale?: string,
  projectsSearch?: boolean
) => {
  // istanbul ignore else
  if (!Object.keys(query).length) {
    return {
      trade: [],
      constructionSector: [],
      companyType: [],
      classifications: [],
      [FilterUrlKey.radius]: {
        ...RADIUS_FILTER,
        value: DEFAULT_SEARCH_RADIUS,
      },
    };
  }
  /* istanbul ignore next */
  const validFilters = await findValidFilters(
    (query[FilterUrlKey.trade]?.split(',') ?? [])
      .concat(query.slug ?? [])
      .filter((f) => f),
    (query[FilterUrlKey.constructionSector]?.split(',') ?? []).filter((f) => f),
    (query[FilterUrlKey.companyType]?.split(',') ?? []).filter((f) => f),
    (query[FilterUrlKey.classification]?.split(',') ?? [])
      .concat(query.slug ?? [])
      .filter((f) => f),
    locale,
    projectsSearch
  );
  /* istanbul ignore next */
  return {
    ...validFilters,
    [FilterUrlKey.radius]: {
      ...RADIUS_FILTER,
      value: Number(query.radius) || DEFAULT_SEARCH_RADIUS,
    },
  };
};

export const getInitialFilterState = (
  validFilters: Partial<FilterSet>,
  enableRadius: boolean,
  immutableFilters: FilterCheckbox[],
  projectsSearch?: boolean
): FilterSet => {
  const filterState = {
    ...filterDefaults(projectsSearch),
    ..._(validFilters).omitBy(_.isNil).value(),
  };
  filterState.radius.disabled = !enableRadius;
  immutableFilters.forEach((filter) => {
    const item = filterState[filter.type]?.find(
      (cb: FilterCheckbox) => cb.key === filter.key
    );
    if (item) {
      item.immutable = true;
    }
  });

  return filterState;
};
